import React, { useEffect, useState } from "react";

interface GroupField {
  key: string;
  label: string;
  type: "text" | "number" | "select" | "email" | "date" | "group";
  options?: string[];
  required?: boolean;
  disable?: boolean;
}

interface GroupFieldProps {
  value: Record<string, any>;
  onChange: (updatedValue: Record<string, any>) => void;
  disabled?: boolean;
  required?: boolean;
  groupFields: GroupField[];
  widthClass?: string;
}

const GroupField: React.FC<GroupFieldProps> = ({
  value,
  onChange,
  disabled,
  groupFields,
  required,
  widthClass = "w-60",
}) => {
  const [groupValue, setGroupValue] = useState<Record<string, any>>({});

  useEffect(() => {
    // Initialize with value or empty fields
    const initial = groupFields.reduce(
      (acc, f) => ({ ...acc, [f.key]: value?.[f.key] ?? "" }),
      {}
    );
    setGroupValue(initial);
  }, [groupFields, value]);

  const handleChange = (key: string, val: any) => {
    const updated = { ...groupValue, [key]: val };
    setGroupValue(updated);
    onChange(updated);
  };

  const isSelectField = (f: GroupField) =>
    f.type === "select" || (f.options && f.options.length > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center">
        {groupFields.map((f) =>
          isSelectField(f) ? (
            <select
              key={f.key}
              value={groupValue[f.key] || ""}
              onChange={(e) => handleChange(f.key, e.target.value)}
              className={`w-32 p-1 h-8 border border-gray-500 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              disabled={disabled || f.disable}
            >
              {f.disable && groupValue[f.key] ? (
                // 🔹 Only option is the current value if disabled
                <option value={groupValue[f.key]}>{groupValue[f.key]}</option>
              ) : (
                <>
                  <option value="">Select {f.label}</option>
                  {(f.options || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </>
              )}
            </select>
          ) : (
            <input
              key={f.key}
              type={f.type}
              value={groupValue[f.key] || ""}
              onChange={(e) =>
                handleChange(
                  f.key,
                  f.type === "number" ? Number(e.target.value) : e.target.value
                )
              }
              placeholder={f.label}
              className={`w-full p-1 h-8 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-200 disabled:cursor-not-allowed`}
              disabled={disabled}
              required={required}
            />
          )
        )}
      </div>
    </div>
  );
};

export default GroupField;
